import { create } from 'zustand';

import { PLACEMENT_STATUS_DEFAULT_TTL_MS, PLACEMENT_STATUS_MAX_EVENTS } from '../constants';
import {
  PlacementStatusConnectionStatus,
  PlacementStatusDisconnectMeta,
  PlacementStatusEvent,
  PlacementStatusMessage,
} from '../types';

interface PlacementStatusState {
  connectionStatus: PlacementStatusConnectionStatus;
  pendingIds: Record<string, number>;
  events: PlacementStatusEvent[];
  lastDisconnect?: PlacementStatusDisconnectMeta;
  trackOrder: (id: string) => void;
  resolveOrder: (message: PlacementStatusMessage) => void;
  expirePending: (ttlMs?: number) => void;
  setConnectionStatus: (status: PlacementStatusConnectionStatus) => void;
  setDisconnectMeta: (meta: PlacementStatusDisconnectMeta | undefined) => void;
  clear: () => void;
}

export const usePlacementStatusStore = create<PlacementStatusState>(set => ({
  connectionStatus: 'idle',
  pendingIds: {},
  events: [],
  trackOrder: id => {
    if (!id) return;
    set(state => ({
      pendingIds: {
        ...state.pendingIds,
        [id]: Date.now(),
      },
    }));
  },
  resolveOrder: message => {
    if (!message.id) return;
    set(state => {
      const nextPending = { ...state.pendingIds };
      delete nextPending[message.id];

      const event: PlacementStatusEvent = {
        ...message,
        receivedAt: Date.now(),
      };

      const nextEvents = [...state.events, event].slice(-PLACEMENT_STATUS_MAX_EVENTS);

      return {
        pendingIds: nextPending,
        events: nextEvents,
      };
    });
  },
  expirePending: (ttlMs = PLACEMENT_STATUS_DEFAULT_TTL_MS) => {
    const limit = Date.now() - ttlMs;
    set(state => {
      const nextPending: Record<string, number> = {};
      Object.entries(state.pendingIds).forEach(([id, ts]) => {
        if (ts >= limit) {
          nextPending[id] = ts;
        }
      });
      return { pendingIds: nextPending };
    });
  },
  setConnectionStatus: status => set({ connectionStatus: status }),
  setDisconnectMeta: meta => set({ lastDisconnect: meta }),
  clear: () =>
    set({
      pendingIds: {},
      events: [],
      lastDisconnect: undefined,
    }),
}));

export const placementStatusSelectors = {
  trackOrder: (state: PlacementStatusState) => state.trackOrder,
  resolveOrder: (state: PlacementStatusState) => state.resolveOrder,
  pendingIds: (state: PlacementStatusState) => state.pendingIds,
  events: (state: PlacementStatusState) => state.events,
  connectionStatus: (state: PlacementStatusState) => state.connectionStatus,
  expirePending: (state: PlacementStatusState) => state.expirePending,
  setConnectionStatus: (state: PlacementStatusState) => state.setConnectionStatus,
  setDisconnectMeta: (state: PlacementStatusState) => state.setDisconnectMeta,
  clear: (state: PlacementStatusState) => state.clear,
  lastDisconnect: (state: PlacementStatusState) => state.lastDisconnect,
};
